import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from 'lucide-react'
import { MobXProviderContext } from 'mobx-react'
import { Controller } from 'react-hook-form'

import { Button } from '~/client/src/shared/components/Button/Button'

import Localization from '../../../localization/LocalizationManager'
import ProjectDateStore from '../../../stores/ui/ProjectDate.store'
import {
  parseDateValue,
  toMidnightTimestamp,
} from '../../../utils/dateValueParser'
import CommonDatePicker from '../../CommonDatePicker/CommonDatePicker'
import MonthPicker from '../../CommonDatePicker/MonthPicker/MonthPicker'
import Dimmer from '../../Dimmer'
import { CalendarIcon } from '../../icons/CalendarIcon'
import StruxhubInput from '../../StruxhubInputs/StruxhubInput'
import { DateTimeFieldConfig } from '../types'

import '../../PermitDatePicker/PermitDatePicker.scss'

interface DateTimeFieldProps {
  field: DateTimeFieldConfig
  control: any
  error?: string
  hideHelperText?: boolean
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
}

export default function DateTimeField({
  field,
  control,
  error,
  hideHelperText,
}: DateTimeFieldProps) {
  const { projectDateStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={field.defaultValue ?? null}
      rules={{
        required: field.required
          ? `${field.label || field.name} is required`
          : false,
      }}
      render={({ field: { onChange, value, name } }) => (
        <DatePickerField
          field={field}
          value={value}
          name={name}
          error={error}
          hideHelperText={hideHelperText}
          projectDateStore={projectDateStore}
          onChange={onChange}
        />
      )}
    />
  )
}

interface IDatePickerFieldProps {
  field: DateTimeFieldConfig
  value: string | number | null | undefined
  name: string
  error?: string
  hideHelperText?: boolean
  projectDateStore?: ProjectDateStore | null
  onChange: (value: number | null) => void
}

function DatePickerField({
  field,
  value,
  name,
  error,
  hideHelperText,
  projectDateStore,
  onChange,
}: IDatePickerFieldProps) {
  const selectedDate = React.useMemo(
    () => parseDateValue(value, projectDateStore),
    [projectDateStore, value],
  )
  const [isOpen, setIsOpen] = React.useState(false)
  const isLocked = !!(field.disabled || field.readOnly || field.displayOnly)
  const [isMonthPickerShown, setIsMonthPickerShown] = React.useState(false)
  const [visibleMonth, setVisibleMonth] = React.useState<Date>(
    selectedDate || new Date(),
  )

  React.useEffect(() => {
    if (isOpen) {
      setVisibleMonth(selectedDate || new Date())
    }
  }, [isOpen, selectedDate])

  const closePicker = React.useCallback(() => setIsOpen(false), [])

  const openPicker = React.useCallback(() => {
    if (!isLocked) {
      setIsOpen(true)
    }
  }, [isLocked])

  const handleTriggerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openPicker()
      }
    },
    [openPicker],
  )

  const handleDateClick = React.useCallback(
    (_event: Event, date: Date) => {
      onChange(toMidnightTimestamp(date, projectDateStore))
      setVisibleMonth(date)
      closePicker()
    },
    [closePicker, onChange, projectDateStore],
  )

  const goToPrevMonth = React.useCallback(() => {
    setVisibleMonth(current =>
      projectDateStore ? projectDateStore.addMonths(current, -1) : current,
    )
  }, [projectDateStore])

  const goToNextMonth = React.useCallback(() => {
    setVisibleMonth(current =>
      projectDateStore ? projectDateStore.addMonths(current, 1) : current,
    )
  }, [projectDateStore])

  const showMonthPicker = React.useCallback(
    () => setIsMonthPickerShown(true),
    [],
  )
  const hideMonthPicker = React.useCallback(
    () => setIsMonthPickerShown(false),
    [],
  )
  const onMonthPickerChange = React.useCallback((month: Date) => {
    setVisibleMonth(month)
  }, [])

  const displayValue =
    selectedDate && projectDateStore
      ? projectDateStore.getWeekdayMonthDayAndYearToDisplay(selectedDate)
      : ''
  const monthLabel =
    projectDateStore?.getMonthAndYearToDisplay(visibleMonth) || ''

  return (
    <div>
      <div
        role={isLocked ? undefined : 'button'}
        tabIndex={isLocked ? -1 : 0}
        className="tw:outline-none"
        onClick={openPicker}
        onKeyDown={handleTriggerKeyDown}
      >
        <StruxhubInput
          label={field.label}
          placeholder={field.placeholder || Localization.translator.selectDate}
          type="text"
          value={displayValue}
          onChange={undefined}
          isRequired={field.required}
          isDisabled={field.disabled}
          isReadOnly={true}
          isValid={!error}
          validationMessage={error}
          isHelperTextHidden={hideHelperText}
          name={name}
          customRightIcon={
            <CalendarIcon size="sm" className="tw:text-muted-foreground" />
          }
        />
      </div>
      {isOpen && (
        <div className="permit-date-picker-container">
          <Dimmer shown={true} onClick={closePicker} />
          <div className="permit-date-picker bg-white no-select col full-width overflow-auto">
            <div className="row y-center pt15 px12">
              <div className="no-grow ml8" onClick={closePicker}>
                <XIcon size={16} className="close-icon pointer" />
              </div>
              <div className="text bold small-header center mr30">
                {Localization.translator.selectDate}
              </div>
            </div>
            <div className="row px16 my20">
              <div>
                <div onClick={showMonthPicker}>
                  <span className="text bold extra-large line-extra-large lp015 primary">
                    {monthLabel}
                  </span>
                  <ChevronDownIcon size={16} />
                </div>
                <MonthPicker
                  shown={isMonthPickerShown}
                  value={visibleMonth}
                  onHide={hideMonthPicker}
                  onChange={onMonthPickerChange}
                />
              </div>
              <div className="row x-end no-flex-children">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={Localization.translator.previous}
                  onClick={goToPrevMonth}
                >
                  <ChevronLeftIcon className="tw:size-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={Localization.translator.next}
                  onClick={goToNextMonth}
                >
                  <ChevronRightIcon className="tw:size-6" />
                </Button>
              </div>
            </div>
            <CommonDatePicker
              monthDate={visibleMonth}
              dateClickHandler={handleDateClick}
              startDate={selectedDate || undefined}
              endDate={selectedDate || undefined}
              isOneDayMode={true}
            />
            <div className="px16 mt5">
              <div
                onClick={handleDateClick.bind(null, null, new Date())}
                className="h40 brada4 bg-palette-blue-scale row x-center y-center text white extra-large bold pointer"
              >
                {Localization.translator.today}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
